import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileDrop from "@/components/admin/FileDrop";
import { Trash2, Pencil, Plus, Eye, EyeOff } from "lucide-react";

const EMPTY = { outlet: "", pub_date: "", image: "", caption: "", published: false };
function Field({ label, children }) { return <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">{label}</label>{children}</div>; }
const fmtDate = (iso) => {
  const d = new Date((iso || "") + "T00:00:00");
  return isNaN(d) ? iso : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
};

const NEW = "__new";
function OutletPicker({ value, outlets, onChange }) {
  const [adding, setAdding] = useState(false);
  const known = outlets.includes(value);
  const showInput = adding || (value && !known) || outlets.length === 0;
  return (
    <div className="space-y-2">
      {outlets.length > 0 && (
        <select value={showInput ? NEW : value} data-testid="clip-outlet-select"
          onChange={(e) => { const v = e.target.value; setAdding(v === NEW); onChange(v === NEW ? "" : v); }}
          className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="" disabled>Select newspaper / outlet…</option>
          {outlets.map((o) => <option key={o} value={o}>{o}</option>)}
          <option value={NEW}>+ Add new newspaper name</option>
        </select>
      )}
      {showInput && <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="e.g. Dainik Jagran" data-testid="clip-outlet-input" autoFocus={adding} />}
    </div>
  );
}

export default function MediaClipsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/media-clips/all").then(({ data }) => setItems(data)).catch(() => {});
  const [outlets, setOutlets] = useState([]);
  const loadOutlets = () => api.get("/media-clips/outlets").then(({ data }) => setOutlets(data)).catch(() => {});
  useEffect(() => { load(); loadOutlets(); }, []);

  const edit = (c) => { setF({ ...EMPTY, ...c }); setOpen(true); };
  const save = async () => {
    if (!f.image) return toast.error("Upload or paste an image first");
    if (!f.outlet.trim()) return toast.error("Newspaper / media outlet name is required");
    if (!f.pub_date) return toast.error("Publication date is required");
    try {
      if (f.id) await api.put(`/media-clips/${f.id}`, f);
      else await api.post("/media-clips", f);
      toast.success("Saved"); setOpen(false); setF(EMPTY); load(); loadOutlets();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (c) => { if (!window.confirm("Delete this clip?")) return; await api.delete(`/media-clips/${c.id}`); toast.success("Deleted"); load(); };
  const togglePub = async (c) => { try { await api.put(`/media-clips/${c.id}`, { published: !c.published }); load(); } catch { toast.error("Could not update"); } };

  return (
    <>
      <AdminHeader title="Media Clips" subtitle={`${items.length} clip${items.length !== 1 ? "s" : ""}`} actions={
        <Button onClick={() => { setF(EMPTY); setOpen(true); }} className="bg-npst-navy text-white" data-testid="add-clip-btn"><Plus size={15} className="mr-1.5" /> Add Media Clip</Button>
      } />
      <AdminPage>
        <p className="text-sm text-slate-500 mb-4">Upload newspaper cuttings or media photos — or click the upload box and paste a copied screenshot (Ctrl+V / Cmd+V). Published clips appear in the gallery on the public Media page.</p>
        {items.length === 0 ? <p className="text-slate-400 text-sm">No clips yet.</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="clip-admin-card">
                <div className="bg-slate-50 flex items-center justify-center h-44 p-2">
                  <img src={c.image} alt={c.outlet} className="max-h-full max-w-full w-auto h-auto object-contain" />
                </div>
                <div className="px-4 py-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-npst-ink truncate">{c.outlet}</span>
                      {c.published
                        ? <span className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid="clip-published-badge">Published</span>
                        : <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded" data-testid="clip-draft-badge">Draft</span>}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{c.pub_date ? fmtDate(c.pub_date) : "No date"}</div>
                  </div>
                  <div className="flex items-center gap-3 text-slate-400 shrink-0">
                    <button onClick={() => togglePub(c)} title={c.published ? "Unpublish" : "Publish"} className="hover:text-npst-navy" data-testid="clip-publish-toggle">{c.published ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                    <button onClick={() => edit(c)} className="hover:text-npst-navy" data-testid="clip-edit-btn"><Pencil size={15} /></button>
                    <button onClick={() => del(c)} className="hover:text-red-600" data-testid="clip-delete-btn"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminPage>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{f.id ? "Edit" : "Add"} Media Clip</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Clip Image (upload, drag & drop, or paste a copied screenshot)">
              <FileDrop value={f.image} onChange={(v) => set("image", v)} label="Drag & drop, click to browse — or click here and press Ctrl+V / Cmd+V to paste" />
            </Field>
            <Field label="Newspaper / Media Outlet Name">
              <OutletPicker key={`${open}-${f.id || "new"}`} value={f.outlet} outlets={outlets} onChange={(v) => set("outlet", v)} />
            </Field>
            <Field label="Headline / Caption (optional — searchable in the archive)">
              <Input value={f.caption || ""} onChange={(e) => set("caption", e.target.value)} placeholder="e.g. Trust files PIL in High Court" data-testid="clip-caption-input" />
            </Field>
            <Field label="Publication Date">
              <Input type="date" value={f.pub_date || ""} onChange={(e) => set("pub_date", e.target.value)} data-testid="clip-date-input" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!f.published} onChange={(e) => set("published", e.target.checked)} data-testid="clip-publish-checkbox" />
              Publish immediately (visible on the Media page)
            </label>
            <Button onClick={save} className="w-full bg-npst-navy text-white" data-testid="clip-save-btn">Save Clip</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
